import React, { useRef, useEffect } from 'react';

const SummaryStat: React.FC<{
    title: string;
    value: number;
    icon?: React.ReactNode;
    variant?: 'default' | 'accent';
    sub?: string;
    // Extra key/value metrics rendered below the value, separated by a rule.
    // Used by the Idle card to surface Total Idle Days + Avg per Unit.
    metrics?: { label: string; value: string }[];
    onClick?: () => void;
    isActive?: boolean;
}> = ({ title, value, icon, variant = 'default', sub, metrics, onClick, isActive }) => {
    const borderClass = variant === 'accent' ? 'border-purple-600/20' : 'border-gray-200';
    const bgClass = 'bg-white';
    const valueRef = useRef<HTMLDivElement | null>(null);
    const [isOverflow, setIsOverflow] = React.useState(false);

    useEffect(() => {
        const el = valueRef.current;
        if (!el) return;

        const timer = setTimeout(() => {
            setIsOverflow(el.scrollWidth > el.clientWidth + 2);
        }, 0);

        return () => clearTimeout(timer);
    }, [value]);

    const safeValue = Number.isFinite(value) ? value : 0;
    const formattedValue = safeValue.toLocaleString('en-US');

    return (
        <div
            role={onClick ? "button" : "group"}
            className={`flex flex-col justify-between rounded-2xl border ${borderClass} ${bgClass} p-3 shadow-sm min-h-[70px] h-full
                transition-colors duration-200 ease-in-out ${
                onClick ? 'cursor-pointer hover:border-purple-300 hover:bg-purple-50/30' : 'cursor-default'
            } ${
                isActive ? 'border-purple-500 bg-purple-50/60' : ''
            }`}
            onClick={onClick}
            onKeyDown={onClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            } : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-pressed={onClick && isActive ? isActive : undefined}
        >
            <div>
                <div className="flex items-start justify-start gap-3 min-w-0">
                    {icon && <div className="text-lg text-neutral-900 flex-shrink-0 w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center">{icon}</div>}
                    <div className="flex-1 min-w-0">
                        <div className="text-base text-neutral-600">{title}</div>
                        <div
                            ref={valueRef}
                            className={`mt-1 font-medium text-gray-900 leading-tight tabular-nums ${isOverflow ? 'text-base break-words line-clamp-2' : 'text-[32px] whitespace-nowrap'
                                }`}
                            title={formattedValue}
                        >
                            {formattedValue}
                        </div>
                    </div>
                </div>
            </div>
            {sub && <div className="text-xs text-gray-400 mt-2">{sub}</div>}
            {metrics && metrics.length > 0 && (
                <div className="mt-2 pt-2 border-t border-amber-200 grid grid-cols-2 gap-2">
                    {metrics.map((m) => (
                        <div key={m.label}>
                            <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
                                {m.label}
                            </div>
                            <div className="text-xs font-bold text-amber-900 mt-0.5 tabular-nums">
                                {m.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SummaryStat;
